import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { FileText, Calendar, User } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface Resume {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
  file_name?: string;
  job_title?: string;
  company_name?: string;
}

interface Step1ResumeSelectionProps {
  selectedResumeId: string;
  onResumeSelect: (resumeId: string) => void;
}

export const Step1ResumeSelection: React.FC<Step1ResumeSelectionProps> = ({
  selectedResumeId,
  onResumeSelect
}) => {
  const [resumes, setResumes] = useState<Resume[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchResumes();
  }, []);

  const fetchResumes = async () => {
    try {
      const { data, error } = await supabase
        .from('resumes')
        .select('id, title, created_at, updated_at, file_name, job_title, company_name')
        .order('updated_at', { ascending: false });

      if (error) throw error;
      setResumes(data || []);
    } catch (error) {
      console.error('Error fetching resumes:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex justify-center items-center h-32">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              <p className="mt-2 text-sm text-muted-foreground">Loading resumes...</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (resumes.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Select Resume to Revise
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <FileText className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No Resumes Found</h3>
            <p className="text-muted-foreground mb-4">
              You need to have at least one resume uploaded to use the revision wizard.
            </p>
            <Button variant="outline">
              Upload Resume
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Select Resume to Revise
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Choose the resume you want to update with the revision wizard.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <RadioGroup value={selectedResumeId} onValueChange={onResumeSelect}>
          {resumes.map((resume) => (
            <div key={resume.id} className="space-y-2">
              <div className={`
                flex items-center space-x-3 p-4 rounded-lg border cursor-pointer transition-colors
                ${selectedResumeId === resume.id ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}
              `}>
                <RadioGroupItem value={resume.id} id={resume.id} />
                <Label htmlFor={resume.id} className="flex-1 cursor-pointer">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium">{resume.title}</h3>
                        {resume.job_title && resume.company_name && (
                          <Badge variant="secondary" className="text-xs">
                            {resume.job_title} at {resume.company_name}
                          </Badge>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        {resume.file_name && (
                          <div className="flex items-center gap-1">
                            <FileText className="h-3 w-3" />
                            {resume.file_name}
                          </div>
                        )}
                        
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          Updated {formatDate(resume.updated_at)}
                        </div>
                      </div>
                    </div>
                  </div>
                </Label>
              </div>
            </div>
          ))}
        </RadioGroup>

        {selectedResumeId && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-2">
              <div className="h-4 w-4 bg-blue-500 rounded-full mt-0.5 flex-shrink-0"></div>
              <div className="text-sm">
                <p className="font-medium text-blue-900">Resume Selected</p>
                <p className="text-blue-700">
                  This resume will be loaded into the revision wizard. You'll be able to upload additional documents and add revision notes in the next step.
                </p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};